import React, { ReactElement, useEffect, useContext, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { TabSelector } from 'src/components/molecules';
import ButtonForward from 'src/components/atoms/ButtonForward';
import { arrCarSelectTypes } from 'src/utils/data';
import { ICarMeta, CarSelectType, QuoteStep } from 'src/types';
import { checkPlateNumber, checkVin } from 'src/api/quote';
import mixPanel from 'src/utils/mixpanel';
import { MIXPANEL_TRACK } from 'src/utils/consts';
import FormPlateNumber from './FormPlateNumber';
import FormYearMakeModel from './FormYearMakeModel';
import FormVin from './FormVin';
import FormConfirmCar from './FormConfirmCar';
import { QuoteContext } from '../../QuoteContext';
import HasCarAlreadyConfirm from './HasCarAlreadyConfirm';

interface SearchCarProps {
  className?: string;
  onConfirm?: () => void;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: 23,
    lineHeight: '28px',
    fontWeight: 800,
    marginBottom: theme.spacing(3),
  },

  contentContainer: {
    flexGrow: 1,
  },

  actionContainer: {
    margin: '10px 0 60px 0',
    width: '100%',
    minHeight: 70,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',

    '& .button-mobile-info': {
      width: 45,
      height: 45,
    },
  },
  buttonBack: {
    marginRight: theme.spacing(1),
  },
  buttonContinue: {
    maxWidth: 150,
    float: 'right',
  },
}));

const SearchCar = (props: SearchCarProps): ReactElement => {
  const { className, onConfirm } = props;

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [carSelectType, setCarSelectType] = useState(
    Object.keys(arrCarSelectTypes)[0]
  );

  const { car, handleSetCar, handleSetStep, requestInProgress, isLoggedIn } =
    useContext(QuoteContext);
  const { serviceDeskTab } = useContext(QuoteContext);
  const isDiagnosisFlow = serviceDeskTab === 0;
  const nonDiagnosisCarSelectTypes = {
    [CarSelectType.BY_VIN]: 'VIN',
    [CarSelectType.BY_PLATE_NUMBER]: 'License Plate + State',
  };
  const carSelectTypes = isDiagnosisFlow
    ? arrCarSelectTypes
    : nonDiagnosisCarSelectTypes;

  const isReadyToSearch =
    (carSelectType === CarSelectType.BY_VIN && !!car.search.vin) ||
    (carSelectType === CarSelectType.BY_PLATE_NUMBER &&
      !!car.search.plate_number &&
      !!car.search.state) ||
    (carSelectType === CarSelectType.BY_YEAR_MAKE_MODEL &&
      !!car.attributes.year &&
      !!car.attributes.make &&
      !!car.attributes.model &&
      !!car.attributes.engine_size);
  const [isReadyToConfirm, setIsReadyToConfirm] = useState(false);

  useEffect(() => {
    const defaultType = isDiagnosisFlow
      ? CarSelectType.BY_YEAR_MAKE_MODEL
      : CarSelectType.BY_VIN;

    if (!Object.keys(carSelectTypes).includes(carSelectType)) {
      setCarSelectType(defaultType);
      setIsReadyToConfirm(false);
    }
  }, [isDiagnosisFlow, carSelectType, carSelectTypes]);

  const onCarOptionSelected = (key: string) => {
    setIsReadyToConfirm(false);

    setCarSelectType(key);
  };

  const handleBack = () => {
    if (isReadyToConfirm) setIsReadyToConfirm(false);
    else handleSetStep(QuoteStep.QUOTE_SERVICE_DESK);
  };

  const showCommonError = () => {
    enqueueSnackbar('Error occured in searching your car.', {
      variant: 'error',
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
    });
  };

  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (carSelectType === CarSelectType.BY_VIN) {
      if (!car.search.vin) return;

      setSearching(true);

      checkVin(car.search.vin)
        .then((resp) => {
          if (!resp || resp.errors || !resp.year) {
            return;
          }

          handleSetCar({
            ...car,
            attributes: {
              year: resp.year,
              make: resp.make,
              model: resp.model,
              engine_size: resp.engine,
              mileage: '',
              vin: resp.vin || car.search.vin,
              last_oil_change: '',
            },
          });

          setIsReadyToConfirm(true);
        })
        .catch(() => showCommonError())
        .finally(() => setSearching(false));
    } else if (carSelectType === CarSelectType.BY_PLATE_NUMBER) {
      if (!car.search.plate_number || !car.search.state) return;

      setSearching(true);

      checkPlateNumber(car.search.plate_number, car.search.state)
        .then((resp) => {
          if (!resp || !resp.specifications) {
            return;
          }

          const carMeta: ICarMeta = resp.specifications;

          if (!carMeta) return;

          handleSetCar({
            ...car,
            attributes: {
              year: carMeta.year,
              make: carMeta.make,
              model: carMeta.model,
              engine_size: carMeta.engine,
              mileage: '',
              vin: carMeta.vin,
              last_oil_change: '',
            },
          });

          setIsReadyToConfirm(true);
        })
        .catch(() => showCommonError())
        .finally(() => setSearching(false));
    } else {
      if (
        !car.attributes.year ||
        !car.attributes.make ||
        !car.attributes.model ||
        !car.attributes.engine_size
      )
        return;

      setIsReadyToConfirm(true);
      if (onConfirm) onConfirm();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
  };

  useEffect(() => {
    mixPanel(MIXPANEL_TRACK.CAR);
  });

  const hasCarAlready = isLoggedIn && typeof car.attributes.id !== 'undefined';

  const renderCarContent = () => {
    if (hasCarAlready) {
      return <HasCarAlreadyConfirm show type={carSelectType} />;
    }

    if (!isReadyToConfirm && carSelectType === CarSelectType.BY_VIN) {
      return <FormVin show />;
    }

    if (!isReadyToConfirm && carSelectType === CarSelectType.BY_PLATE_NUMBER) {
      return <FormPlateNumber show />;
    }

    if (carSelectType === CarSelectType.BY_YEAR_MAKE_MODEL) {
      return <FormYearMakeModel show />;
    }

    return <FormConfirmCar show type={carSelectType} />;
  };

  return (
    <Box className={clsx('quote-search-car', classes.root, className)}>
      <Typography className={classes.title}>
        {hasCarAlready ? 'Confirm your car' : 'Tell us about your car'}
      </Typography>
      {!hasCarAlready && !isDiagnosisFlow && (
        <Typography style={{ marginBottom: 10 }}>
          Choose one: VIN <b>or</b> License Plate + State.
        </Typography>
      )}
      <Grid container className={classes.contentContainer}>
        <Grid item md={8} sm={12} xs={12}>
          {hasCarAlready && (
            <Button
              style={{ margin: '10px 0' }}
              color="primary"
              variant="outlined"
              onClick={() =>
                handleSetCar({
                  search: {
                    vin: '',
                    plate_number: '',
                    state: '',
                  },
                  attributes: {
                    year: '',
                    make: '',
                    model: '',
                    engine_size: '',
                    mileage: '',
                    vin: '',
                    last_oil_change: '',
                  },
                })
              }
            >
              Use a different car
            </Button>
          )}
          {!hasCarAlready && (
            <TabSelector
              items={carSelectTypes}
              onTabSelected={onCarOptionSelected}
              selectedValue={carSelectType}
            />
          )}
          {renderCarContent()}
        </Grid>
        <Grid item md={4} sm={12} xs={12} className={classes.actionContainer}>
          <ButtonForward
            title="Back"
            key="Back"
            color="default"
            rounded
            noIcon
            transparent
            size="large"
            onClickHandler={handleBack}
            className={classes.buttonBack}
          />
          {!isReadyToConfirm && !hasCarAlready ? (
            <ButtonForward
              title={
                carSelectType === CarSelectType.BY_PLATE_NUMBER ||
                carSelectType === CarSelectType.BY_VIN
                  ? 'Search'
                  : 'Continue'
              }
              key={
                carSelectType === CarSelectType.BY_PLATE_NUMBER ||
                carSelectType === CarSelectType.BY_VIN
                  ? 'Search'
                  : 'Continue'
              }
              rounded
              size="large"
              disabled={searching || !isReadyToSearch}
              onClickHandler={handleSearch}
              processing={searching}
              className={classes.buttonContinue}
            />
          ) : (
            <ButtonForward
              title="Confirm"
              rounded
              size="large"
              onClickHandler={handleConfirm}
              disabled={requestInProgress}
              processing={requestInProgress}
              className={classes.buttonContinue}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

SearchCar.defaultProps = {
  className: undefined,
  onConfirm: undefined,
};

export default SearchCar;
